import React, { useState } from 'react';
import { CheckCircle2, XCircle, GraduationCap, Briefcase } from 'lucide-react';
import { confirmProfileAdd } from '../../api/chatbot';

const SECTION_META = {
    education: { label: 'Education', icon: GraduationCap },
    experience: { label: 'Experience', icon: Briefcase },
};

const summarizeRecord = (section, record) => {
    if (section === 'education') {
        const range = [record.startYear, record.endYear].filter(Boolean).join(' – ');
        return {
            title: record.degree,
            subtitle: [record.institution, record.fieldOfStudy].filter(Boolean).join(' · '),
            meta: range,
        };
    }
    if (section === 'experience') {
        // startMonth/endMonth are full month names (e.g. "June") — the
        // existing profile-update endpoint requires names, not numbers.
        const start = record.startMonth ? `${record.startMonth} ${record.startYear}` : record.startYear;
        const end = record.isCurrent ? 'Present' : (record.endMonth ? `${record.endMonth} ${record.endYear}` : record.endYear);
        return {
            title: record.designation,
            subtitle: [record.company, record.location].filter(Boolean).join(' · '),
            meta: [start, end].filter(Boolean).join(' – '),
        };
    }
    return { title: '', subtitle: '', meta: '' };
};

// Multi-field variant of ProfileUpdateConfirmCard — for record-style
// additions (education/experience) rather than a single-value diff
// (plan Phase 8). Same safety model: Confirm is the only path that writes.
const ProfileAddConfirmCard = ({ section, proposedRecord }) => {
    const [status, setStatus] = useState('pending'); // pending | saving | saved | error | cancelled

    if (status === 'cancelled') {
        return <p className="ml-9 text-xs text-gray-400 italic">Not added.</p>;
    }

    const meta = SECTION_META[section] || { label: section, icon: GraduationCap };
    const Icon = meta.icon;

    if (status === 'saved') {
        return (
            <div className="rounded-xl border border-green-200 bg-green-50 p-3 flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 size={16} /> {meta.label} added to your profile.
            </div>
        );
    }

    const { title, subtitle, meta: metaLine } = summarizeRecord(section, proposedRecord || {});

    const handleConfirm = async () => {
        setStatus('saving');
        try {
            const res = await confirmProfileAdd(section, proposedRecord);
            setStatus(res?.responseCode === 200 ? 'saved' : 'error');
        } catch {
            setStatus('error');
        }
    };

    return (
        <div className="rounded-xl border border-talexo-blue/30 bg-talexo-blue/10 p-4">
            <div className="flex items-center gap-1 text-xs text-talexo-blue">
                <Icon size={12} /> Add to {meta.label}
            </div>
            <p className="text-sm font-semibold text-talexo-blue-dark">{title}</p>
            {subtitle && <p className="text-xs text-talexo-blue-dark">{subtitle}</p>}
            {metaLine && <p className="text-xs text-talexo-blue">{metaLine}</p>}

            {status === 'error' && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-600"><XCircle size={12} /> Couldn't add this — please try again.</p>
            )}

            <div className="mt-3 flex gap-2">
                <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={status === 'saving'}
                    className="flex-1 rounded-lg bg-talexo-blue px-3 py-2 text-sm font-medium text-white hover:bg-talexo-blue-dark disabled:opacity-50"
                >
                    {status === 'saving' ? 'Saving…' : 'Confirm'}
                </button>
                <button
                    type="button"
                    onClick={() => setStatus('cancelled')}
                    disabled={status === 'saving'}
                    className="flex-1 rounded-lg border border-talexo-blue/40 bg-white px-3 py-2 text-sm font-medium text-talexo-blue-dark hover:bg-talexo-blue/10 disabled:opacity-50"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default ProfileAddConfirmCard;
